import { fromAssigneeHistoryDataList } from './AssigneeHistoryData.js';
import { fromCommentDataList } from './CommentData.js';
import { fromPatientData } from './PatientData.js';
import {
  asBoolean,
  asInstant,
  mapDtoList,
  optionalEnum,
  optionalString,
  requireObject,
  requireString,
} from './parse.js';

/**
 * Mirrors com.medprompt.task.OutcomeTaskType
 */
export const OutcomeTaskType = Object.freeze({
  REFERRAL: 'REFERRAL',
  ADMIN_NOTE: 'ADMIN_NOTE',
  PRESCRIPTION_CHANGE: 'PRESCRIPTION_CHANGE',
  PATIENT_NOTE: 'PATIENT_NOTE',
});

/**
 * Mirrors com.medprompt.task.OutcomeTask
 *
 * @typedef {object} OutcomeTaskData
 * @property {string} taskId
 * @property {string|null} text
 * @property {string|null} quote
 * @property {string|null} taskType
 * @property {string|null} assigneeId
 * @property {import('./AssigneeHistoryData.js').AssigneeHistoryData[]} assigneeHistory
 * @property {string|null} documentId
 * @property {string|null} consultationId
 * @property {import('./PatientData.js').PatientData|null} patient
 * @property {import('./CommentData.js').CommentData[]} comments
 * @property {boolean} isSelected
 * @property {boolean} isUrgent
 * @property {boolean} isComplete
 * @property {number|string|null} timestamp
 */

/**
 * @param {object} raw
 * @returns {OutcomeTaskData}
 */
export function fromOutcomeTaskData(raw) {
  const source = requireObject(raw, 'OutcomeTask');
  return {
    taskId: requireString(source, 'taskId', 'OutcomeTask'),
    text: optionalString(source.text),
    quote: optionalString(source.quote),
    taskType: optionalEnum(source.taskType, OutcomeTaskType, 'OutcomeTask', 'taskType'),
    assigneeId: optionalString(source.assigneeId),
    assigneeHistory: fromAssigneeHistoryDataList(source.assigneeHistory),
    documentId: optionalString(source.documentId),
    consultationId: optionalString(source.consultationId),
    patient: source.patient ? fromPatientData(source.patient) : null,
    comments: fromCommentDataList(source.comments),
    isSelected: asBoolean(source.isSelected),
    isUrgent: asBoolean(source.isUrgent),
    isComplete: asBoolean(source.isComplete),
    timestamp: asInstant(source.timestamp),
  };
}

/**
 * @param {unknown} raw
 * @returns {OutcomeTaskData[]}
 */
export function fromOutcomeTaskDataList(raw) {
  return mapDtoList(raw, 'OutcomeTask', fromOutcomeTaskData);
}

/**
 * Mirrors com.medprompt.task.OutcomeTaskUpdate.
 * Omitted fields are left off the body; Jackson treats a missing boolean as false.
 *
 * @param {object} fields
 * @returns {object}
 */
export function toOutcomeTaskUpdate(fields) {
  if (typeof fields?.taskId !== 'string' || !fields.taskId) {
    throw new Error('OutcomeTaskUpdate: taskId is required');
  }
  const body = { taskId: fields.taskId };
  if (fields.text !== undefined) body.text = fields.text;
  if (fields.taskType !== undefined) body.taskType = fields.taskType;
  if (fields.assigneeId !== undefined) body.assigneeId = fields.assigneeId;
  if (fields.isSelected !== undefined) body.isSelected = fields.isSelected;
  if (fields.isDeselected !== undefined) body.isDeselected = fields.isDeselected;
  if (fields.isUrgent !== undefined) body.isUrgent = fields.isUrgent;
  if (fields.isNotUrgent !== undefined) body.isNotUrgent = fields.isNotUrgent;
  return body;
}

/**
 * Mirrors com.medprompt.task.OutcomeTaskRequest
 *
 * @param {object} fields
 * @returns {{ text: string|null, taskType: string|null, assigneeId: string|null, isUrgent: boolean }}
 */
export function toOutcomeTaskRequest(fields) {
  return {
    text: optionalString(fields?.text),
    taskType: optionalString(fields?.taskType),
    assigneeId: optionalString(fields?.assigneeId),
    isUrgent: asBoolean(fields?.isUrgent),
  };
}
